import { useState, useEffect, useRef, useCallback } from 'react';
import { IkigaiBuckets, JourneyEntry, CareerPrototypeDoc, UserStats } from '../types';
import { loadUserProfile, saveUserProfile, deleteUserProfile, addJourneyEntry, getJourneyTimeline, getCareerPrototypes } from '../services/dbService';
import { migrateLegacyChats } from '../services/dbService';
import { useKai } from '../contexts/KaiPresenceContext';

interface UseIkigaiDataProps {
    user: any;
}

// Helper: Calculate days between two timestamps
const daysBetween = (t1: number, t2: number) => {
    const d1 = new Date(t1);
    const d2 = new Date(t2);
    d1.setHours(0,0,0,0);
    d2.setHours(0,0,0,0);
    return Math.round((d2.getTime() - d1.getTime()) / (1000 * 60 * 60 * 24));
};

export const useIkigaiData = ({ user }: UseIkigaiDataProps) => {
    const { setArchetype, setColor, setIsEnabled, state: kaiState } = useKai();

    // --- State ---
    const [ikigaiData, setIkigaiData] = useState<IkigaiBuckets>({
        passion: [], mission: [], profession: [], vocation: [], drains: []
    });
    const [careerPrototypes, setCareerPrototypes] = useState<CareerPrototypeDoc[]>([]);
    const [currentQuest, setCurrentQuest] = useState<string | null>(null);
    const [isQuestCompleted, setIsQuestCompleted] = useState(false);
    const [journeyHistory, setJourneyHistory] = useState<JourneyEntry[]>([]);
    
    // Memory 2.0: Facts
    const [userFacts, setUserFacts] = useState<string[]>([]);
    
    // Gamification (Sprint 3)
    const [userStats, setUserStats] = useState<UserStats>({
        streak: 1, maxStreak: 1, lastVisit: Date.now(), xp: 0, level: 1
    });
    
    const [isDataHydrating, setIsDataHydrating] = useState(true);
    const [saveStatus, setSaveStatus] = useState<'saved' | 'saving'>('saved');
    
    const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // --- 1. Hydration ---
    useEffect(() => {
        const hydrate = async () => {
            if (!user || !user.uid) {
                setIsDataHydrating(false);
                return;
            }

            setIsDataHydrating(true);
            try {
                // Migration Check (Safe to run often, returns early if done)
                try { await migrateLegacyChats(user.uid); } catch (e) { console.warn(e); }

                // Load Profile
                const profile = await loadUserProfile(user.uid);
                
                if (profile) {
                    const safeIkigaiData = profile.ikigaiData || {
                        passion: [], mission: [], profession: [], vocation: [], drains: []
                    };
                    setIkigaiData(safeIkigaiData);
                    
                    // Explicitly set facts, defaulting to empty array if undefined
                    setUserFacts(profile.facts || []);

                    // Sync Preferences to Kai Context
                    if (profile.preferences) {
                        if (profile.preferences.kaiEnabled !== undefined) setIsEnabled(profile.preferences.kaiEnabled);
                        if (profile.preferences.kaiArchetype) setArchetype(profile.preferences.kaiArchetype);
                        if (profile.preferences.kaiColor) setColor(profile.preferences.kaiColor);
                    }

                    // --- STREAK LOGIC (Sprint 3) ---
                    if (profile.stats) {
                        const now = Date.now();
                        const last = profile.stats.lastVisit || now;
                        const diff = daysBetween(last, now);
                        
                        let newStreak = profile.stats.streak;
                        let newMax = profile.stats.maxStreak;

                        if (diff === 1) {
                            // Consecutive day!
                            newStreak += 1;
                            newMax = Math.max(newStreak, newMax);
                        } else if (diff > 1) {
                            // Missed a day
                            newStreak = 1;
                        }
                        // If diff === 0, same day, do nothing.

                        setUserStats({
                            ...profile.stats,
                            streak: newStreak,
                            maxStreak: newMax,
                            lastVisit: now
                        });
                    }
                }

                // Load Subcollections
                const prototypes = await getCareerPrototypes(user.uid);
                setCareerPrototypes(prototypes);

                const timeline = await getJourneyTimeline(user.uid);
                setJourneyHistory(timeline);

            } catch (e) {
                console.error("Hydration failed", e);
                setIkigaiData({ passion: [], mission: [], profession: [], vocation: [], drains: [] });
            } finally {
                setIsDataHydrating(false);
            }
        };
        hydrate();
    }, [user, setArchetype, setColor, setIsEnabled]);

    // --- 2. Auto-Save (Debounced) ---
    useEffect(() => {
        if (!user || !user.uid || isDataHydrating) return;

        if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
        setSaveStatus('saving');
        
        // Reduced debounce from 2000ms to 800ms to catch updates before user navigates away
        saveTimeoutRef.current = setTimeout(async () => {
            try {
                // Save simplified paths for lightweight profile access
                const simplePaths = careerPrototypes.map(p => ({
                    title: p.title,
                    description: p.description,
                    matchScore: p.matchScore,
                    reasoning: p.reasoning
                }));

                console.log("💾 [Auto-Save] Saving profile...", { factsCount: userFacts.length, streak: userStats.streak });

                await saveUserProfile(user.uid, {
                    ikigaiData,
                    careerPaths: simplePaths,
                    facts: userFacts, // Save Memory 2.0 Facts
                    stats: userStats, // Save Gamification
                    preferences: {
                        theme: localStorage.getItem('kai_theme') === 'dark' ? 'dark' : 'light',
                        kaiEnabled: kaiState.isEnabled,
                        kaiArchetype: kaiState.archetype,
                        kaiColor: kaiState.color
                    },
                    lastUpdated: Date.now()
                });
                setSaveStatus('saved');
            } catch (e) {
                console.error("Auto-save failed", e);
                setSaveStatus('saved');
            }
        }, 800);

        return () => {
            if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
        };
    }, [user, ikigaiData, careerPrototypes, isDataHydrating, kaiState, userFacts, userStats]);

    // --- 3. Actions ---

    const saveJourneyItem = useCallback(async (entry: Omit<JourneyEntry, 'id' | 'createdAt'>) => {
        if (!user || !user.uid) return;
        const tempId = Date.now().toString();
        const fullEntry: JourneyEntry = {
            id: tempId,
            createdAt: Date.now(),
            ...entry
        };
        setJourneyHistory(prev => [fullEntry, ...prev]);
        
        // Award XP for Journey Actions
        setUserStats(prev => ({
            ...prev,
            xp: prev.xp + 50,
            level: Math.floor((prev.xp + 50) / 500) + 1
        }));

        await addJourneyEntry(user.uid, entry);
    }, [user]);

    const updatePrototypeState = useCallback((careerTitle: string, updates: Partial<CareerPrototypeDoc>) => {
        setCareerPrototypes(prev => prev.map(p => {
            if (p.title === careerTitle) {
                return { ...p, ...updates };
            }
            return p;
        }));
    }, []);

    const resetData = useCallback(async () => {
        if (!user || !user.uid) return;
        await deleteUserProfile(user.uid);
        setIkigaiData({ passion: [], mission: [], profession: [], vocation: [], drains: [] });
        setUserFacts([]);
        setCareerPrototypes([]);
        setJourneyHistory([]);
        setCurrentQuest(null);
        setUserStats({ streak: 1, maxStreak: 1, lastVisit: Date.now(), xp: 0, level: 1 });
    }, [user]);

    const mergeBuckets = useCallback((updates: Partial<IkigaiBuckets> & { facts?: string[], remove?: Partial<IkigaiBuckets> }) => {
        setIkigaiData(prev => {
            
            // Helper: Normalize item for comparison
            const normalize = (s: string) => s.trim().toLowerCase();

            // 1. Identify Implicit Conflicts
            // For Passion/Mission: Mutually Exclusive with Drains.
            // For Profession/Vocation: NOT exclusive (The "Competence Trap").
            const addedToDrains = (updates.drains || []).map(normalize);
            const addedToLove = [
                ...(updates.passion || []),
                ...(updates.mission || [])
            ].map(normalize);

            // 2. Resolve final list for a specific bucket
            // logic: (Current + Incoming) - Removals - ImplicitConflicts
            const resolveBucket = (
                bucketName: keyof IkigaiBuckets, 
                currentItems: string[], 
                newItems: string[] | undefined,
                explicitRemovals: string[] | undefined
            ) => {
                const incoming = newItems || [];
                const removals = explicitRemovals || [];
                
                const normalizedCurrent = currentItems.map(normalize);
                const normalizedRemovals = removals.map(normalize);

                // Start with existing items
                let result = [...currentItems];

                // Add new items (if not already present)
                incoming.forEach(item => {
                    const norm = normalize(item);
                    if (!normalizedCurrent.includes(norm) && item.trim().length > 0) {
                        result.push(item.trim());
                    }
                });

                // Apply Removals (Filter)
                result = result.filter(item => {
                    const norm = normalize(item);
                    
                    // A. Explicit Removal (from AI instruction)
                    if (normalizedRemovals.includes(norm)) return false;

                    // B. Implicit Conflict: PASSION/MISSION cannot exist in DRAINS
                    // However, PROFESSION/VOCATION *can* exist in Drains (Competence Trap).
                    const isLoveBucket = bucketName === 'passion' || bucketName === 'mission';
                    if (isLoveBucket && addedToDrains.includes(norm)) return false;

                    // C. Implicit Conflict: DRAINS cannot contain things recently added to PASSION/MISSION
                    // (Recency rule: If I say I love it now, remove it from drains)
                    if (bucketName === 'drains' && addedToLove.includes(norm)) return false;

                    return true;
                });

                return result;
            };

            return {
               passion: resolveBucket('passion', prev.passion, updates.passion, updates.remove?.passion),
               mission: resolveBucket('mission', prev.mission, updates.mission, updates.remove?.mission),
               profession: resolveBucket('profession', prev.profession, updates.profession, updates.remove?.profession),
               vocation: resolveBucket('vocation', prev.vocation, updates.vocation, updates.remove?.vocation),
               drains: resolveBucket('drains', prev.drains, updates.drains, updates.remove?.drains)
            };
        });

        // Merge Facts with deduplication
        if (updates.facts) {
            console.log("📥 [Merge] New facts detected:", updates.facts);
            setUserFacts(prev => {
                const normalizedExisting = new Set(prev.map(f => f.trim().toLowerCase()));
                const newFacts = [...prev];
                
                updates.facts!.forEach(fact => {
                    const cleanFact = fact.trim();
                    const normalized = cleanFact.toLowerCase();
                    if (cleanFact && !normalizedExisting.has(normalized)) {
                        normalizedExisting.add(normalized);
                        newFacts.push(cleanFact);
                    }
                });
                return newFacts;
            });
            
            // Award XP for Facts
            setUserStats(prev => ({ ...prev, xp: prev.xp + 20 }));
        }
    }, []);

    const removeFact = useCallback((factToRemove: string) => {
        // 1. Remove from Facts
        setUserFacts(prev => prev.filter(f => f !== factToRemove));

        // 2. Cascading Deletion from Buckets
        // If a fact is deleted (e.g. "Hates Coding"), we should remove "Coding" from the Drains bucket.
        // We use a regex word boundary check to see if the bucket item exists inside the deleted fact.
        setIkigaiData(prev => {
            const next = { ...prev };
            const normalize = (s: string) => s.toLowerCase().trim();
            const targetFact = normalize(factToRemove);

            (Object.keys(next) as Array<keyof IkigaiBuckets>).forEach(key => {
                next[key] = next[key].filter(bucketItem => {
                    const itemNorm = normalize(bucketItem);
                    
                    // Case A: Exact Match (e.g. Fact: "Coding", Bucket: "Coding")
                    if (itemNorm === targetFact) return false;

                    // Case B: Semantic Containment (e.g. Fact: "Hates Coding", Bucket: "Coding")
                    // We check if the bucket item appears as a WHOLE WORD in the deleted fact.
                    const regex = new RegExp(`\\b${itemNorm}\\b`, 'i');
                    if (regex.test(targetFact)) {
                        console.log(`🗑️ [Cascade Delete] Removed '${bucketItem}' from '${key}' because fact '${factToRemove}' was deleted.`);
                        return false; 
                    }

                    return true;
                });
            });
            return next;
        });
    }, []);

    // Load Demo Data (Dev Tool)
    const loadDemoData = useCallback(() => {
        setIkigaiData({
            passion: ["Photography", "Storytelling", "Nature"],
            mission: ["Climate Awareness", "Mental Health"],
            profession: ["Marketing Manager", "Copywriter", "Editing"],
            vocation: ["Content Strategy", "Public Speaking", "Social Media"],
            drains: ["Micromanagement", "Data Entry"]
        });
        setUserFacts(["Lives in Portland", "Has a dog named Luna", "Hates cold calling"]);
        const demoCareers: CareerPrototypeDoc[] = [
            {
                id: 'environmental-photojournalist',
                createdAt: Date.now(),
                title: "Environmental Photojournalist",
                description: "Combine your photography skills with your mission for climate awareness to document the changing planet.",
                matchScore: 92,
                reasoning: ["Uses Passion for Photography", "Aligns with Climate Mission"],
                bucketMatches: {
                    passion: ["Photography", "Nature"],
                    mission: ["Climate Awareness"],
                    profession: ["Editing"],
                    vocation: ["Content Strategy"]
                }
            },
            {
                id: 'therapeutic-art-facilitator',
                createdAt: Date.now() - 100000,
                title: "Therapeutic Art Facilitator",
                description: "A life path focused on leveraging the meditative and expressive power of painting to help individuals process emotions.",
                matchScore: 90,
                reasoning: ["Uses Passion for Art", "Aligns with Mental Health"],
                bucketMatches: {
                    passion: ["Storytelling"],
                    mission: ["Mental Health"],
                    profession: ["Marketing Manager"],
                    vocation: ["Public Speaking"]
                }
            }
        ];
        setCareerPrototypes(demoCareers);
        setUserStats(prev => ({ ...prev, streak: 3, xp: 1250, level: 3 }));
    }, []);

    return {
        ikigaiData,
        setIkigaiData,
        userFacts,
        removeFact,
        userStats, // Export stats
        careerPrototypes,
        setCareerPrototypes,
        currentQuest,
        setCurrentQuest,
        isQuestCompleted,
        setIsQuestCompleted,
        journeyHistory,
        setJourneyHistory,
        isDataHydrating,
        saveStatus,
        saveJourneyItem,
        updatePrototypeState,
        resetData,
        mergeBuckets,
        loadDemoData
    };
};